// src/pages/system/ServiceAdmin.jsx
import { useEffect, useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { requireUser } from '../../context/Sessions';

/* ⬇️ 신규 생성 모달에서 쓰는 위젯/훅 */
import FileInput from '../widgets/FileInput';
import HelpPopover from '../widgets/HelpPopover';
import ModalProgress from '../widgets/ModalProgress';
import useUploadWithProgress from '../../utils/useUploadWithProgress';

import svcStyles from './Service.module.css';

/* ====== 아이콘 ====== */
const Gear = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path
            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.592c.55 0 1.02.398 1.11.94l.213 1.274c.063.374.313.686.659.84.346.154.746.142 1.08-.032l1.12-.592a1.125 1.125 0 0 1 1.37.253l1.834 2.27a1.125 1.125 0 0 1-.105 1.494l-.956.917c-.278.267-.39.66-.29 1.03.101.37.382.665.746.77l1.278.368c.538.155.9.65.9 1.21v2.592c0 .56-.362 1.055-.9 1.21l-1.278.368a1.125 1.125 0 0 1-.746.77c-.364.105-.768 0-1.046-.267l-.956-.917a1.125 1.125 0 0 0-1.494-.105l-1.12.592c-.334.174-.734.186-1.08.032a1.125 1.125 0 0 1-.659-.84l-.213-1.274a1.125 1.125 0 0 0-1.11-.94H10.7a1.125 1.125 0 0 0-1.11.94l-.213 1.274a1.125 1.125 0 0 1-.659.84 1.125 1.125 0 0 1-1.08-.032l-1.12-.592a1.125 1.125 0 0 0-1.494.105l-.956.917a1.125 1.125 0 0 1-1.046.267 1.125 1.125 0 0 1-.746-.77L2.09 17.3A1.125 1.125 0 0 1 1.5 16.09v-2.592c0-.56.362-1.055.9-1.21l1.278-.368c.364-.105.645-.4.746-.77.1-.37-.012-.763-.29-1.03l-.956-.917A1.125 1.125 0 0 1 3.083 7.33l1.834-2.27a1.125 1.125 0 0 1 1.37-.253l1.12.592c.334.174.734.186 1.08.032.346-.154.596-.466.659-.84l.213-1.274Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const Trash = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M9.75 9.75v7.5M14.25 9.75v7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.5 6.75h15M10.5 4.5h3a1.5 1.5 0 0 1 1.5 1.5v0H9a1.5 1.5 0 0 1 1.5-1.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.75 6.75V18A1.5 1.5 0 0 0 8.25 19.5h7.5A1.5 1.5 0 0 0 17.25 18V6.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const Plus = (p) => (
    <svg viewBox="0 0 24 24" width="44" height="44" fill="currentColor" {...p}>
        <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
    </svg>
);

/* ====== 목록 로더 (유지) ====== */
export async function loader({ request }) {
    const { role, tenantId } = await requireUser(request);
    const base = new URL('/api/service/model_list', request.url);

    if (role === 'ADMIN' || role === 'PERSONAL') {
        base.searchParams.set('tenantId', String(tenantId));
        base.searchParams.set('ownedOnly', 'true');
    }

    const res = await fetch(base.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'include',
    });

    if (!res.ok) throw new Response('Failed to load services', { status: res.status });
    const data = await res.json();

    const normalized = Array.isArray(data)
        ? data.map((item, i) => {
              const id = String(item.key ?? item.id ?? item.service_id ?? item.name ?? `svc-${i + 1}`);
              const image = `/uploads/${encodeURIComponent(item.imageUrl)}`;
              return {
                  id,
                  name: item.name,
                  image,
                  plans: item.plans,
                  raw: item,
              };
          })
        : [];
    return { services: normalized, role, tenantId };
}

/* ====== 편집 모달 ====== */
function EditModal({ model, onClose, onSaved }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [prices, setPrices] = useState([]);

    const prettyName = useMemo(() => model?.name ?? `모델 ${model?.id}`, [model]);

    useEffect(() => {
        let abort = false;
        (async () => {
            try {
                const res = await fetch(`/api/service/model_price/${model.id}`, {
                    headers: { Accept: 'application/json' },
                    credentials: 'include',
                });
                if (!res.ok) throw new Error('Failed to load prices');
                const data = await res.json();
                if (!abort) setPrices(Array.isArray(data) ? data : []);
            } catch (e) {
                alert('요금 정보를 불러오지 못했습니다.');
            } finally {
                if (!abort) setLoading(false);
            }
        })();
        return () => {
            abort = true;
        };
    }, [model?.id]);

    const addRow = () => setPrices((p) => [...p, { key: undefined, model_key: Number(model.id), name: '', price: 0 }]);
    const removeRow = (i) => setPrices((p) => p.filter((_, idx) => idx !== i));
    const updateRow = (i, patch) => setPrices((p) => p.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

    const handleSave = async () => {
        for (const r of prices) {
            if (!r.name || String(r.name).trim() === '') return alert('요금 이름을 입력하세요.');
            if (Number.isNaN(Number(r.price))) return alert('가격은 숫자여야 합니다.');
        }
        try {
            setSaving(true);
            const payload = prices.map((p) => ({
                key: p.key ?? null,
                model_key: Number(model.id),
                name: String(p.name),
                price: Number(p.price),
            }));
            const res = await fetch(`/api/service/model_price/${model.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Save failed');
            onSaved?.();
            onClose?.();
        } catch (e) {
            alert('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // ESC
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose?.();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className={svcStyles.editModalOverlay} onClick={onClose}>
            <div className={svcStyles.editModal} onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div className={svcStyles.editModalHead}>
                    <div className={svcStyles.editModalTitle}>
                        <div className={svcStyles.editModalAvatar}>{(prettyName || 'S').slice(0, 1).toUpperCase()}</div>
                        <div className={svcStyles.editModalTitleText}>
                            <div className={svcStyles.editModalName}>{prettyName}</div>
                            <div className={svcStyles.editModalSub}>요금 정책 편집</div>
                        </div>
                    </div>
                </div>

                {/* 바디 */}
                <div className={svcStyles.editModalBody}>
                    {loading ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                            <div
                                style={{
                                    height: 16,
                                    width: 180,
                                    borderRadius: 8,
                                    background: '#eef2f7',
                                }}
                            />
                            <div
                                style={{
                                    height: 14,
                                    width: '100%',
                                    borderRadius: 8,
                                    background: '#f3f6fb',
                                }}
                            />
                            <div
                                style={{
                                    height: 14,
                                    width: '100%',
                                    borderRadius: 8,
                                    background: '#f3f6fb',
                                }}
                            />
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: 12, width: '100%' }}>
                            <table className={svcStyles.priceTable}>
                                <thead>
                                    <tr>
                                        <th>이름</th>
                                        <th>가격(원)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prices.map((r, i) => (
                                        <tr key={r.key ?? `new-${i}`}>
                                            <td>
                                                <input
                                                    value={r.name ?? ''}
                                                    onChange={(e) =>
                                                        updateRow(i, {
                                                            name: e.target.value,
                                                        })
                                                    }
                                                    className={svcStyles.priceInput}
                                                    placeholder='예: "1달", "1년", "종량제", "체험판"'
                                                />
                                            </td>
                                            <td className={svcStyles.priceCell}>
                                                <input
                                                    value={r.price ?? 0}
                                                    onChange={(e) =>
                                                        updateRow(i, {
                                                            price: e.target.value.replace(/[^\d]/g, ''),
                                                        })
                                                    }
                                                    className={`${svcStyles.priceInput} ${svcStyles.priceInputNumeric}`}
                                                    inputMode="numeric"
                                                    placeholder="예: 10000"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* 푸터 */}
                <div className={svcStyles.editModalFoot}>
                    <button className={svcStyles.modalBtn} onClick={onClose} disabled={saving}>
                        취소
                    </button>
                    <button className={`${svcStyles.modalBtn} ${svcStyles.modalBtnPrimary}`} onClick={handleSave} disabled={saving || loading}>
                        {saving ? '저장 중…' : '저장'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ====== 신규 생성 모달 ====== */
function CreateModal({ onClose, onSaved }) {
    const [serviceName, setServiceName] = useState('');
    const [serviceImage, setServiceImage] = useState(null);
    const [shape3d, setShape3d] = useState(null);
    const [floorsJson, setFloorsJson] = useState(null);
    const [floors3d, setFloors3d] = useState([]);
    const [networkJson, setNetworkJson] = useState(null);
    const [sectionJson, setSectionJson] = useState(null);
    const [inputJson, setInputJson] = useState(null);

    const [planName, setPlanName] = useState('모델요금 구성');
    const [planPrice, setPlanPrice] = useState(''); // "10000"처럼 숫자만 저장

    const { upload, cancel, uploading, progress, mbps, etaSec } = useUploadWithProgress({
        endpoint: '/api/system/model_new',
        withCredentials: true,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!serviceName.trim()) return alert('서비스 이름을 입력하세요.');
        if (!shape3d) return alert('3D 모델 형상을 업로드하세요.');
        if (!inputJson) return alert('모델 입력 파라메터(JSON)를 업로드하세요.');
        if (!floorsJson) return alert('층정보(JSON)를 업로드하세요.');
        if (!networkJson) return alert('네트워크정보(JSON)를 업로드하세요.');
        if (!sectionJson) return alert('섹션정보(JSON)를 업로드하세요.');
        if (!planName.trim()) return alert('요금제 이름을 입력하세요.');
        if (Number.isNaN(Number(planPrice))) return alert('요금제 가격은 숫자여야 합니다.');

        const fd = new FormData();
        fd.append('serviceName', serviceName);
        if (serviceImage) fd.append('serviceImage', serviceImage);
        fd.append('shape3d', shape3d);
        fd.append('inputJson', inputJson);
        fd.append('floorsJson', floorsJson);
        fd.append('networkJson', networkJson);
        fd.append('sectionJson', sectionJson);
        if (Array.isArray(floors3d)) floors3d.forEach((f) => fd.append('floors3d', f));

        const plansPayload = [{ name: String(planName), price: Number(planPrice || '0') }];
        fd.append('plans', JSON.stringify(plansPayload));

        try {
            const res = await upload(fd);
            alert('신규 모델이 추가되었습니다.');
            onSaved?.();
            onClose?.();
        } catch (err) {
            if (String(err?.message || '').includes('취소')) return;
            alert('업로드 중 오류: ' + err.message);
        }
    };

    // ESC
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose?.();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className={svcStyles.createOverlay} onClick={onClose}>
            <div className={svcStyles.createSheet} onClick={(e) => e.stopPropagation()}>
                <div className={svcStyles.createSheetInner}>
                    <form className={svcStyles.createFormPanel} onSubmit={handleSubmit}>
                        <div className={svcStyles.createFormGrid}>
                            {/* 서비스 */}
                            <label className={svcStyles.createVLabel}>서비스</label>
                            <div className={svcStyles.createFilesGrid}>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>서비스 이름</span>
                                    </div>
                                    <input
                                        type="text"
                                        value={serviceName}
                                        onChange={(e) => setServiceName(e.target.value)}
                                        className={`${svcStyles.largeInput} ${svcStyles.serviceName}`}
                                    />
                                </div>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>서비스 이미지</span>
                                    </div>
                                    <FileInput accept="image/*" onFileChange={setServiceImage} value={serviceImage} showPreview />
                                </div>
                            </div>

                            <div className={svcStyles.formSeparator} aria-hidden="true" />

                            {/* 모델 파일 */}
                            <label className={svcStyles.createVLabel}>모델</label>
                            <div className={svcStyles.createFilesGrid}>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>3D 모델 형상</span>
                                        <HelpPopover title="3D 모델 업로드 가이드">권장: .glb / .gltf(zip)</HelpPopover>
                                    </div>
                                    <FileInput accept=".glb,.gltf,.zip" required onFileChange={setShape3d} value={shape3d} />
                                </div>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>층별 3D 모델</span>
                                        <HelpPopover title="층별 3D 모델">층별 glb 다중 업로드 가능</HelpPopover>
                                    </div>
                                    <FileInput accept=".glb,.gltf,.zip" multiple onFileChange={setFloors3d} value={floors3d} />
                                </div>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>모델 입력 파라메터(JSON)</span>
                                        <HelpPopover title="모델 입력 파라메터">json 형식</HelpPopover>
                                    </div>
                                    <FileInput accept=".json,application/json" required onFileChange={setInputJson} value={inputJson} />
                                </div>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>층정보(JSON)</span>
                                        <HelpPopover title="층정보(JSON)">{`예) [{"floor":"1","y0":0,"thickness":0.3}]`}</HelpPopover>
                                    </div>
                                    <FileInput accept=".json,application/json" required onFileChange={setFloorsJson} value={floorsJson} />
                                </div>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>네트워크정보(JSON)</span>
                                        <HelpPopover title="네트워크 구성 정보">json 형식</HelpPopover>
                                    </div>
                                    <FileInput accept=".json,application/json" required onFileChange={setNetworkJson} value={networkJson} />
                                </div>
                                <div className={svcStyles.createFileCell}>
                                    <div className={svcStyles.createCellHead}>
                                        <span>섹션정보(JSON)</span>
                                        <HelpPopover title="섹션 구성 정보">json 형식</HelpPopover>
                                    </div>
                                    <FileInput accept=".json,application/json" required onFileChange={setSectionJson} value={sectionJson} />
                                </div>
                            </div>

                            <div className={svcStyles.formSeparator} aria-hidden="true" />

                            {/* 요금제 */}
                            <label className={svcStyles.createVLabel}>요금제</label>

                            <div className={svcStyles.planAreaSingle}>
                                {/* 요금제 이름 */}
                                <div className={svcStyles.planRowSingle}>
                                    <input
                                        className={svcStyles.edInput}
                                        value={planName}
                                        onChange={(e) => setPlanName(e.target.value)}
                                        placeholder="예: 기본 요금제"
                                        aria-label="요금제 이름"
                                    />
                                </div>

                                {/* 요금제 가격 */}
                                <div className={svcStyles.planRowSingle}>
                                    <div className={svcStyles.withUnit}>
                                        <input
                                            className={`${svcStyles.edInput} ${svcStyles.planPrice}`}
                                            value={planPrice ? Number(planPrice).toLocaleString() : ''}
                                            onChange={(e) => {
                                                const digits = e.target.value.replace(/[^\d]/g, '');
                                                setPlanPrice(digits);
                                            }}
                                            inputMode="numeric"
                                            placeholder="예: 10000"
                                            aria-label="요금제 가격(원)"
                                        />
                                        <span className={svcStyles.priceSuffix}>원</span>
                                    </div>
                                </div>
                                <div className={svcStyles.formActions}>
                                    <button type="submit" className="btn btn--primary" disabled={uploading}>
                                        {uploading ? '업로드 중…' : '확인'}
                                    </button>
                                    <button type="button" className="btn btn--ghost" onClick={onClose} disabled={uploading}>
                                        취소
                                    </button>
                                </div>
                            </div>
                        </div>

                        <ModalProgress
                            open={uploading}
                            percent={progress}
                            speedMBps={mbps}
                            etaSec={etaSec}
                            onCancel={cancel}
                            title="모델 파일을 업로드 중입니다"
                            subtitle="업로드가 끝나면 자동으로 저장을 완료합니다."
                        />
                    </form>
                </div>
            </div>
        </div>
    );
}

/* ====== 페이지 ====== */
export default function ServiceAdmin() {
    const { services, role } = useLoaderData();
    const revalidator = useRevalidator();

    const [editTarget, setEditTarget] = useState(null); // 편집 대상
    const [createOpen, setCreateOpen] = useState(false); // 신규 생성 모달

    const totalCount = services.length;

    const onEdit = (model) => {
        setEditTarget({
            id: model.id ?? model.key,
            name: model.name,
            image: model.image,
            raw: model.raw ?? model,
        });
    };

    const onDelete = async (id) => {
        if (!window.confirm('해당 서비스를 삭제하시겠습니까?')) return;
        try {
            const res = await fetch(`/api/system/model_delete/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.ok === false) {
                alert(data?.message || '삭제 실패');
                return;
            }
            alert('삭제되었습니다.');
            revalidator.revalidate();
        } catch (e) {
            alert('삭제 요청 중 오류가 발생했습니다.');
        }
    };

    const onCreate = () => {
        setCreateOpen(true);
    };

    return (
        <main className={svcStyles.wrap}>
            {/* 헤더 */}
            <header className={svcStyles.pageHeader}>
                <h1 className={svcStyles.pageTitle}>서비스 관리</h1>
            </header>

            {/* 등록된 서비스 목록 섹션 */}
            <section className={svcStyles.section}>
                <div className={svcStyles.sectionHead}>
                    <h2>등록된 서비스</h2>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                        }}
                    >
                        <span className={svcStyles.sectionMeta}>
                            총 <b>{totalCount}</b>개
                        </span>
                        <button type="button" className="btn btn--primary" onClick={onCreate}>
                            신규 서비스
                        </button>
                    </div>
                </div>

                {services.length === 0 ? (
                    <p style={{ fontSize: 14, color: '#6B7280' }}>
                        등록된 서비스가 없습니다. &nbsp;
                        <button type="button" className="btn btn--primary" onClick={onCreate}>
                            첫 서비스 등록
                        </button>
                    </p>
                ) : (
                    <div className={svcStyles.myTable}>
                        {/* 헤더 행 */}
                        <div className={`${svcStyles.myRow} ${svcStyles.myRowHeader}`}>
                            <div className={svcStyles.myCellName}>서비스</div>
                            <div className={svcStyles.myCellStatus}>요금 정보</div>
                            <div className={svcStyles.myCellPrice}>요금제 개수</div>
                            <div className={svcStyles.myCellAction}>관리</div>
                        </div>

                        {/* 데이터 행 */}
                        {services.map((s) => {
                            const plans = Array.isArray(s.plans) ? s.plans : [];
                            const firstPlan = plans[0] ?? null;
                            const priceText = firstPlan ? `${firstPlan.name ?? '기본'} · ${Number(firstPlan.price ?? 0).toLocaleString()}원` : '등록된 요금제가 없습니다';

                            return (
                                <div key={s.id} className={svcStyles.myRow}>
                                    {/* 서비스 (썸네일 + 이름) */}
                                    <div className={svcStyles.myCellName}>
                                        <div className={svcStyles.admServiceCell}>
                                            {s.image && <img src={s.image} alt="" className={svcStyles.admThumb} />}
                                            <div className={svcStyles.admServiceText}>
                                                <div className={svcStyles.admServiceName}>{s.name}</div>
                                                <div className={svcStyles.admServiceMeta}>ID: {s.id}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 요금 정보 요약 */}
                                    <div className={svcStyles.myCellStatus}>
                                        <span className={svcStyles.statusBadgeActive}>{priceText}</span>
                                    </div>

                                    {/* 요금제 개수 */}
                                    <div className={svcStyles.myCellPrice}>{plans.length}개</div>

                                    {/* 액션 버튼들 */}
                                    <div className={svcStyles.myCellAction}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: 6,
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <button type="button" className="btn btn--ghost" onClick={() => onEdit(s)} title="요금 정책 편집">
                                                <Gear style={{ marginRight: 4 }} />
                                                요금 편집
                                            </button>
                                            <button
                                                type="button"
                                                className="btn"
                                                style={{
                                                    color: '#B91C1C',
                                                    borderColor: '#fecaca',
                                                }}
                                                onClick={() => onDelete(s.id)}
                                            >
                                                <Trash style={{ marginRight: 4 }} />
                                                삭제
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* 모달들 */}
            {editTarget && (
                <EditModal
                    model={editTarget}
                    onClose={() => setEditTarget(null)}
                    onSaved={() => {
                        revalidator.revalidate();
                    }}
                />
            )}

            {createOpen && (
                <CreateModal
                    onClose={() => setCreateOpen(false)}
                    onSaved={() => {
                        revalidator.revalidate();
                    }}
                />
            )}
        </main>
    );
}
